import React, { useState } from 'react';
import { View, StyleSheet, TouchableOpacity, ActivityIndicator, TextInput, KeyboardAvoidingView, Platform } from 'react-native';
import { Alert } from '../../utils/toast';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../theme/ThemeContext';
import { ClubsStackParamList } from '../../types/navigation';
import ThemedText from '../../components/common/ThemedText';
import { verifyCodeAndJoin } from '../../lib/api/clubs';
import { showSuccessToast, showErrorToast } from '../../utils/toast';

type EnterClubCodeNavigationProp = StackNavigationProp<ClubsStackParamList, 'EnterClubCode'>;
type EnterClubCodeRouteProp = RouteProp<ClubsStackParamList, 'EnterClubCode'>;

const EnterClubCodeScreen: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation<EnterClubCodeNavigationProp>();
  const route = useRoute<EnterClubCodeRouteProp>();
  const { clubId, clubName } = route.params;

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerifyCode = async () => {
    if (!code.trim()) {
      Alert.alert('Required', 'Please enter the club code');
      return;
    }

    if (code.trim().length !== 8) {
      Alert.alert('Invalid Code', 'Club code must be 8 characters');
      return;
    }

    try {
      setLoading(true);
      await verifyCodeAndJoin(clubId, code.trim());
      showSuccessToast(`Welcome to ${clubName}!`);
      
      // Navigate back to club detail
      navigation.navigate('ClubDetail', { clubId });
    } catch (error: any) {
      showErrorToast(error.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right', 'bottom']}>
      <KeyboardAvoidingView 
        style={styles.keyboardView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        {/* Header */}
        <View style={[styles.header, { borderBottomColor: theme.border }]}>
          <TouchableOpacity onPress={() => navigation.goBack()} activeOpacity={0.7}>
            <Ionicons name="arrow-back" size={24} color={theme.text} />
          </TouchableOpacity>
          <ThemedText style={styles.headerTitle}>Enter Club Code</ThemedText>
          <View style={{ width: 24 }} />
        </View>

        {/* Content */}
        <View style={styles.content}>
          <View style={[styles.iconContainer, { backgroundColor: theme.card }]}>
            <Ionicons name="key" size={48} color={theme.accent} />
          </View>

          <ThemedText style={[styles.title, { color: theme.text }]}>
            Enter Club Code
          </ThemedText>

          <ThemedText style={[styles.description, { color: theme.textSecondary }]}>
            Your request to join "{clubName}" has been approved! 
            {'\n\n'}
            Please enter the 8-digit code shared by the admin to complete joining.
          </ThemedText>

          <View style={[styles.inputContainer, { backgroundColor: theme.card, borderColor: theme.border }]}>
            <TextInput
              style={[styles.input, { color: theme.text }]}
              value={code}
              onChangeText={setCode}
              placeholder="Enter 8-digit code"
              placeholderTextColor={theme.textTertiary}
              maxLength={8}
              autoCapitalize="characters"
              autoCorrect={false}
              keyboardType="default"
              editable={!loading}
            />
          </View>

          <TouchableOpacity
            style={[
              styles.submitButton,
              { backgroundColor: theme.accent },
              loading && styles.submitButtonDisabled
            ]}
            onPress={handleVerifyCode}
            disabled={loading}
            activeOpacity={0.7}
          >
            {loading ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <ThemedText style={styles.submitButtonText}>Verify & Join</ThemedText>
            )}
          </TouchableOpacity>

          <View style={[styles.infoBox, { backgroundColor: theme.card, borderColor: theme.border }]}>
            <Ionicons name="information-circle" size={20} color={theme.accent} />
            <ThemedText style={[styles.infoText, { color: theme.textSecondary }]}>
              The admin will share this code with you after payment confirmation. 
              If you haven't received it yet, please contact the club admin.
            </ThemedText>
          </View>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
    padding: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconContainer: {
    width: 96,
    height: 96,
    borderRadius: 48,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 16,
    textAlign: 'center',
  },
  description: {
    fontSize: 16,
    lineHeight: 24,
    textAlign: 'center',
    marginBottom: 32,
  },
  inputContainer: {
    width: '100%',
    borderRadius: 12,
    borderWidth: 1,
    paddingHorizontal: 16,
    marginBottom: 24,
  },
  input: {
    fontSize: 18,
    fontWeight: '600',
    paddingVertical: 16,
    textAlign: 'center',
    letterSpacing: 4,
  },
  submitButton: {
    width: '100%',
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  infoBox: {
    flexDirection: 'row',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
});

export default EnterClubCodeScreen;
